import flatpickr from 'flatpickr';
import { Korean } from 'flatpickr/dist/l10n/ko.js';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index.js';
import 'flatpickr/dist/flatpickr.min.css';
import 'flatpickr/dist/plugins/monthSelect/style.css';

/**
 * Flatpickr bootstrap + Korean locale + skmFlatpickr 헬퍼.
 *
 * 시리즈 B (2026-05-19, CLAUDE.md §UI/UX #4-B): bootstrap-datepicker npm 의존성은
 * Bootstrap 5 전환 단계에서 제거됨. 신규 사용자 코드는 `.datepicker(` 호출 금지.
 * 단, Sentry SKILLSINMATH-326 에서 운영 브라우저가 오래된/동적 jQuery datepicker
 * 호출을 실행하는 사례가 확인되어, bootstrap-datepicker 재도입 없이 flatpickr
 * 기반의 최소 호환 bridge 만 유지한다.
 *
 * 현재 책임 — flatpickr 부트스트랩 인프라 (핵심):
 *   1. window.flatpickr / window.monthSelectPlugin 전역 노출
 *   2. flatpickr Korean locale 적용
 *   3. window.skmFlatpickr 헬퍼 객체 (init/initDay/initMonth/getDate/setDate/open/close)
 *   4. $.fn.datepicker 최소 호환 bridge (legacy 호출 → flatpickr 위임)
 *
 * 사용처:
 *   - layouts/app.blade.php — initSkmFlatpickrInput(this) 가 window.flatpickr 직접 호출
 *   - 화면별 skm-datepicker class markup — window.skmFlatpickr.* 헬퍼 호출
 *   - skm-month-nav__input — window.monthSelectPlugin 직접 사용
 */

window.flatpickr = window.flatpickr || flatpickr;
window.monthSelectPlugin = window.monthSelectPlugin || monthSelectPlugin;

if (window.flatpickr) {
    window.flatpickr.localize(Korean);
}

function resolveInput(target) {
    if (!target) {
        return null;
    }

    if (target.jquery) {
        return resolveInput(target[0] || null);
    }

    if (typeof target === 'string') {
        return document.querySelector(target);
    }

    if (target.tagName && target.tagName.toLowerCase() !== 'input') {
        return target.querySelector('input');
    }

    return target;
}

function toFlatpickrDateFormat(format) {
    if (!format) {
        return 'Y-m-d';
    }

    const tokenMap = {
        yyyy: 'Y',
        yy: 'y',
        dd: 'd',
        d: 'j',
        mm: 'm',
        m: 'n',
    };

    return String(format).replace(/yyyy|yy|dd|d|mm|m/g, function (token) {
        return tokenMap[token] || token;
    });
}

function buildDirectConfig(options = {}) {
    const config = {
        dateFormat: options.dateFormat || toFlatpickrDateFormat(options.format),
        disableMobile: true,
        onChange: options.onChange,
    };

    if (options.startDate) {
        config.minDate = options.startDate;
    }

    if (options.endDate) {
        config.maxDate = options.endDate;
    }

    if (options.appendTo) {
        const appendTo = typeof options.appendTo === 'string'
            ? document.querySelector(options.appendTo)
            : options.appendTo;

        if (appendTo) {
            config.appendTo = appendTo;
        }
    }

    if (options.month || options.minViewMode === 1 || options.minViewMode === 'months') {
        config.plugins = [
            monthSelectPlugin({
                shorthand: false,
                dateFormat: config.dateFormat,
                altFormat: config.dateFormat,
            }),
        ];
    }

    return config;
}

function initDirect(target, options = {}) {
    const input = resolveInput(target);
    if (!input || !window.flatpickr) {
        return null;
    }

    if (input._flatpickr) {
        if (!options.reinit) {
            return input._flatpickr;
        }

        input._flatpickr.destroy();
    }

    return window.flatpickr(input, buildDirectConfig(options));
}

window.skmFlatpickr = window.skmFlatpickr || {
    init: initDirect,
    initDay(target, options = {}) {
        return initDirect(target, {...options, dateFormat: options.dateFormat || 'Y-m-d'});
    },
    initMonth(target, options = {}) {
        return initDirect(target, {...options, month: true, dateFormat: options.dateFormat || 'Y년 m월'});
    },
    getDate(target) {
        const input = resolveInput(target);
        const instance = input && input._flatpickr;

        return instance && instance.selectedDates ? (instance.selectedDates[0] || null) : null;
    },
    setDate(target, value, triggerChange = true) {
        const input = resolveInput(target);
        const instance = input && input._flatpickr;

        if (instance) {
            instance.setDate(value || null, triggerChange);
            return;
        }

        if (input) {
            input.value = value || '';
            if (triggerChange) {
                input.dispatchEvent(new Event('change', {bubbles: true}));
            }
        }
    },
    open(target) {
        const input = resolveInput(target);
        const instance = input && input._flatpickr;

        if (instance) {
            instance.open();
        }
    },
    close(target) {
        const input = resolveInput(target);
        const instance = input && input._flatpickr;

        if (instance) {
            instance.close();
        }
    },
};

function installJqueryDatepickerBridge() {
    const $ = window.jQuery || window.$;
    if (!$ || !$.fn || $.fn.datepicker) {
        return;
    }

    $.fn.datepicker = function (commandOrOptions, ...args) {
        if (typeof commandOrOptions === 'string') {
            const command = commandOrOptions;
            if (command === 'getDate') {
                const input = resolveInput(this[0]);
                return input && input._flatpickr
                    ? (input._flatpickr.selectedDates[0] || null)
                    : null;
            }

            return this.each(function () {
                const input = resolveInput(this);
                const instance = input && input._flatpickr;
                if (!input) {
                    return;
                }

                if (command === 'show') {
                    if (instance) {
                        instance.open();
                    }
                    return;
                }

                if (command === 'hide') {
                    if (instance) {
                        instance.close();
                    }
                    return;
                }

                if (command === 'destroy' || command === 'remove') {
                    if (instance) {
                        instance.destroy();
                    }
                    return;
                }

                if (command === 'setDate' || command === 'update') {
                    if (instance) {
                        instance.setDate(args[0] || null, true);
                    } else {
                        input.value = args[0] || '';
                    }
                }
            });
        }

        const options = commandOrOptions || {};
        return this.each(function () {
            initDirect(this, options);
        });
    };

    $.fn.datepicker.__skmFlatpickrBridge = true;
    $.fn.datepicker.dates = $.fn.datepicker.dates || {};
}

installJqueryDatepickerBridge();
